// src/pages/Utilisateurs/useAdminUsersListing.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getRoles,
  getAllUsers,
  getLatestUsers,
  deleteUser as deleteUserAPI,
  addUserByAdmin,
  updateUser,
} from "./../../services/userServices";

import { getUserCompetitions } from "./../../services/competitionsServices";

import {
  buildRolesMap,
  buildUsersCSV,
  downloadCSV,
  getRoleLabel,
  getUserCreatedDate,
  getUserId,
  normalizeRoleOptions,
  normalizeText,
} from "./usersAdminUtils";

export function useAdminUsersListing() {
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [competitionsMap, setCompetitionsMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [savingRoleId, setSavingRoleId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [competitionFilter, setCompetitionFilter] = useState("all");
  const [sort, setSort] = useState("name_az");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const rolesMap = useMemo(() => buildRolesMap(roleOptions), [roleOptions]);

  const loadCompetitionsForUsers = useCallback(async (rows = []) => {
    const entries = await Promise.all(
      rows.map(async (user) => {
        const id = getUserId(user);
        if (!id) return [id, []];

        try {
          const competitions = await getUserCompetitions(id);
          return [id, Array.isArray(competitions) ? competitions : []];
        } catch {
          return [id, []];
        }
      })
    );

    setCompetitionsMap(Object.fromEntries(entries.filter(([id]) => id)));
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);

      const [usersData, rolesData] = await Promise.all([
        getAllUsers(),
        getRoles(),
      ]);

      const nextUsers = Array.isArray(usersData) ? usersData : [];
      const nextRoles = normalizeRoleOptions(rolesData);

      setUsers(nextUsers);
      setRoleOptions(nextRoles);

      await loadCompetitionsForUsers(nextUsers);
    } catch (error) {
      console.error("Erreur chargement utilisateurs :", error);
      toast.error("Erreur lors du chargement des utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, [loadCompetitionsForUsers]);

  const loadLatest = useCallback(async () => {
    try {
      setLoading(true);

      const latestUsers = await getLatestUsers();
      const nextUsers = Array.isArray(latestUsers) ? latestUsers : [];

      setUsers(nextUsers);
      await loadCompetitionsForUsers(nextUsers);

      toast.success("Derniers inscrits chargés.");
    } catch (error) {
      console.error("Erreur derniers utilisateurs :", error);
      toast.error("Erreur lors de la récupération des derniers utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, [loadCompetitionsForUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const search = normalizeText(q.trim());

    let rows = [...users];

    if (search) {
      rows = rows.filter((user) => {
        const id = getUserId(user);
        const competitions = competitionsMap[id] || [];
        const competitionNames = competitions
          .map((competition) => competition.nom_competition)
          .join(" ");

        const searchable = [
          id,
          user.nom,
          user.prenom,
          user.email,
          getRoleLabel(user.role_id, rolesMap),
          competitionNames,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(search);
      });
    }

    if (roleFilter !== "all") {
      rows = rows.filter(
        (user) => String(user.role_id) === String(roleFilter)
      );
    }

    if (competitionFilter !== "all") {
      rows = rows.filter((user) => {
        const id = getUserId(user);
        const competitions = competitionsMap[id] || [];

        if (competitionFilter === "with") return competitions.length > 0;
        if (competitionFilter === "without") return competitions.length === 0;

        return true;
      });
    }

    rows.sort((a, b) => {
      if (sort === "name_az") {
        return String(a.nom || "").localeCompare(String(b.nom || ""), "fr");
      }

      if (sort === "name_za") {
        return String(b.nom || "").localeCompare(String(a.nom || ""), "fr");
      }

      if (sort === "email_az") {
        return String(a.email || "").localeCompare(String(b.email || ""), "fr");
      }

      if (sort === "role_asc") {
        return Number(a.role_id || 0) - Number(b.role_id || 0);
      }

      if (sort === "date_desc") {
        return (
          (new Date(getUserCreatedDate(b)).getTime() || 0) -
          (new Date(getUserCreatedDate(a)).getTime() || 0)
        );
      }

      return 0;
    });

    return rows;
  }, [
    users,
    q,
    roleFilter,
    competitionFilter,
    sort,
    rolesMap,
    competitionsMap,
  ]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const stats = useMemo(() => {
    const byRole = roleOptions.reduce((acc, role) => {
      acc[role.id] = 0;
      return acc;
    }, {});

    let withCompetitions = 0;

    for (const user of users) {
      const roleId = Number(user.role_id);

      if (byRole[roleId] !== undefined) {
        byRole[roleId] += 1;
      }

      const id = getUserId(user);
      if ((competitionsMap[id] || []).length > 0) {
        withCompetitions += 1;
      }
    }

    return {
      total: users.length,
      filtered: filteredUsers.length,
      byRole,
      withCompetitions,
      withoutCompetitions: users.length - withCompetitions,
    };
  }, [users, filteredUsers, roleOptions, competitionsMap]);

  const openAddDrawer = useCallback(() => {
    setSelectedUser(null);
    setDrawerMode("add");
  }, []);

  const openEditDrawer = useCallback((user) => {
    setSelectedUser(user);
    setDrawerMode("edit");
  }, []);

  const closeUserDrawer = useCallback(() => {
    setSelectedUser(null);
    setDrawerMode(null);
  }, []);

  const saveUser = useCallback(
    async (payload) => {
      try {
        if (drawerMode === "add") {
          await addUserByAdmin(payload);
          toast.success("Utilisateur créé avec succès.");
        }

        if (drawerMode === "edit") {
          const id = getUserId(selectedUser);
          await updateUser(id, payload);
          toast.success("Utilisateur mis à jour avec succès.");
        }

        closeUserDrawer();
        await loadUsers();
      } catch (error) {
        console.error("Erreur sauvegarde utilisateur :", error);
        toast.error("Erreur lors de l'enregistrement de l'utilisateur.");
      }
    },
    [drawerMode, selectedUser, closeUserDrawer, loadUsers]
  );

  const deleteUser = useCallback(
    async (user) => {
      const id = getUserId(user);
      if (!id) return;

      const confirmed = window.confirm(
        `Supprimer définitivement ${user.prenom || ""} ${user.nom || ""} ?`
      );

      if (!confirmed) return;

      try {
        setDeletingId(id);

        const message = await deleteUserAPI(id);
        toast.success(message || "Utilisateur supprimé.");

        await loadUsers();
      } catch (error) {
        console.error("Erreur suppression utilisateur :", error);
        toast.error("Erreur lors de la suppression de l'utilisateur.");
      } finally {
        setDeletingId(null);
      }
    },
    [loadUsers]
  );

  const updateUserRole = useCallback(
    async (user, nextRoleId) => {
      const id = getUserId(user);
      const roleId = Number(nextRoleId);

      if (!id || !Number.isInteger(roleId)) return;
      if (Number(user.role_id) === roleId) return;

      const previousUsers = users;

      try {
        setSavingRoleId(id);

        setUsers((prev) =>
          prev.map((item) =>
            getUserId(item) === id ? { ...item, role_id: roleId } : item
          )
        );

        await updateUser(id, {
          nom: user.nom || "",
          prenom: user.prenom || "",
          email: user.email || "",
          role_id: roleId,
        });

        toast.success("Rôle mis à jour.");
      } catch (error) {
        console.error("Erreur modification rôle :", error);
        setUsers(previousUsers);
        toast.error("Impossible de modifier le rôle.");
      } finally {
        setSavingRoleId(null);
      }
    },
    [users]
  );

  const exportCSV = useCallback(() => {
    const csv = buildUsersCSV(filteredUsers, rolesMap, competitionsMap);
    downloadCSV("utilisateurs-monapplitournoi.csv", csv);
    toast.success("Export CSV généré.");
  }, [filteredUsers, rolesMap, competitionsMap]);

  const resetFilters = useCallback(() => {
    setQ("");
    setRoleFilter("all");
    setCompetitionFilter("all");
    setSort("name_az");
    setPage(1);
  }, []);

  return {
    users,
    filteredUsers,
    pageRows,
    roleOptions,
    rolesMap,
    competitionsMap,
    stats,

    loading,
    savingRoleId,
    deletingId,

    drawerMode,
    selectedUser,
    selectedCompetition,
    setSelectedCompetition,

    q,
    setQ,
    roleFilter,
    setRoleFilter,
    competitionFilter,
    setCompetitionFilter,
    sort,
    setSort,

    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,

    loadUsers,
    loadLatest,
    openAddDrawer,
    openEditDrawer,
    closeUserDrawer,
    saveUser,
    deleteUser,
    updateUserRole,
    exportCSV,
    resetFilters,
  };
}